import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useQuery, useSubscription } from "@apollo/client/react";

import { INVENTORY_ASSET_STOCKED } from "../../../constants/graphql";
import {
  ASSETS_COLLECTION_STORE_QUERY,
  FETCH_PROJECT_QUERY,
} from "../../../constants/graphql/queries";
import Filter from "../../../views/shopping/ProjectAssets/Filter";
import Results from "../../../views/shopping/ProjectAssets/Results";
import { initialValues } from "../../../views/shopping/ProjectAssets/DefaultItems";
import { ShoppingProvider } from "../../../contexts/shopping";
import useAuth from "../../../hooks/useAuth";
import {
  DEFAULT_ADD_MORE_ITEMS_SORT,
  updateAddMoreItemsRoute,
} from "../../shopping/Cart/addMoreItemsRoute";

const DEFAULT_LIMIT = 24;

const parseSelectedIds = (value) => {
  if (!value || value === "all") return [];

  return value.split(",").filter(Boolean);
};

export const ProjectBrowseView = ({
  projectId,
  onItemAdded,
  processNewItem,
  hideSearch,
  centerFilters,
  searchValue,
  cardsPerRow = 4,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routeSearchValue = routeParams.get("sq") || "";
  const availabilityMode = routeParams.get("availability") || "in_stock";
  const routeWarehouseValue = routeParams.get("warehouse");
  const routeCostCenterValue = routeParams.get("costCenter");
  const routeWarehouseIds = useMemo(
    () => parseSelectedIds(routeWarehouseValue),
    [routeWarehouseValue]
  );
  const routeCostCenterIds = useMemo(
    () => parseSelectedIds(routeCostCenterValue),
    [routeCostCenterValue]
  );
  const resolvedSearchValue = searchValue ?? routeSearchValue;
  const [assets, setAssets] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [defaultValues] = useState(initialValues);
  const [filters, setFilters] = useState({
    search: resolvedSearchValue,
    order: DEFAULT_ADD_MORE_ITEMS_SORT,
    page: 1,
    limit: DEFAULT_LIMIT,
    warehouseIds: routeWarehouseIds,
    categories: [],
    costCenterIds: routeCostCenterIds,
  });
  const searchQuery = searchValue || filters.search || "";
  const order = filters.order;
  const page = filters.page;
  const limit = filters.limit;
  const selectedWarehouseIds = filters.warehouseIds;
  const hideDepletedAssets = availabilityMode === "in_stock";
  const hasReservedQuantity = availabilityMode === "reserved";
  const categories = filters.categories;
  const selectedCostCenterIds = filters.costCenterIds;

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      search: resolvedSearchValue,
      warehouseIds: routeWarehouseIds,
      costCenterIds: routeCostCenterIds,
      page: 1,
    }));
  }, [resolvedSearchValue, routeWarehouseIds, routeCostCenterIds]);

  const { data: projectData } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });
  const project = projectData?.project || {};
  const warehouses = project?.warehouses || [];
  const allCostCenters = project?.costCenters || [];
  const userCostCenterIds = user.costCenterIds || [];
  const costCenters =
    userCostCenterIds.length > 0
      ? allCostCenters.filter((cc) => userCostCenterIds.includes(String(cc.id)))
      : allCostCenters;

  const variables = useMemo(() => {
    const values = {
      search: searchQuery,
      storePage: true,
      hideVariants: true,
      limit,
      page,
      categories,
      hideDepletedAssets,
      includeRegularAssetsFromGroupedProjects: false,
    };

    if (projectId) values.projectId = projectId;
    if (selectedWarehouseIds.length) values.warehouseIds = selectedWarehouseIds;
    if (selectedCostCenterIds.length) values.costCenterIds = selectedCostCenterIds;
    if (hasReservedQuantity) values.hasReservedQuantity = true;

    if (order) {
      const [key, value] = order.split(".");
      values.order = { [key]: value };
    }

    return values;
  }, [
    categories,
    selectedCostCenterIds,
    hasReservedQuantity,
    hideDepletedAssets,
    limit,
    order,
    page,
    projectId,
    searchQuery,
    selectedWarehouseIds,
  ]);

  const { data: collectionData, loading } = useQuery(ASSETS_COLLECTION_STORE_QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables,
  });
  const categoryOptions = collectionData?.categories || [];

  useEffect(() => {
    if (collectionData) {
      const { assetsCollection = {} } = collectionData;
      setAssets(assetsCollection?.collection);
      setMetadata(assetsCollection?.metadata || {});
    }
  }, [collectionData]);

  useSubscription(INVENTORY_ASSET_STOCKED, {
    onData: ({ data: { data: { inventoryAssetStocked } = {} } = {} }) => {
      const existingAssetIndex = _.findIndex(
        assets,
        (asset) => asset.id == inventoryAssetStocked.id
      );
      if (existingAssetIndex == null) return;
      setAssets((prevAssets) =>
        Object.assign([], prevAssets, {
          [existingAssetIndex]: inventoryAssetStocked,
        })
      );
    },
  });

  const onChangeFilter = useCallback(
    (newFilter) => {
      const routeUpdates = {};

      if ("search" in newFilter && !hideSearch) {
        routeUpdates.sq = newFilter.search || null;
        routeUpdates.page = null;
      }
      if ("availabilityMode" in newFilter) {
        routeUpdates.availability = newFilter.availabilityMode || "in_stock";
        routeUpdates.page = null;
      }
      if ("warehouseIds" in newFilter) {
        routeUpdates.warehouse = newFilter.warehouseIds.length ? newFilter.warehouseIds : "all";
        routeUpdates.page = null;
      }
      if ("costCenterIds" in newFilter) {
        routeUpdates.costCenter = newFilter.costCenterIds.length ? newFilter.costCenterIds : null;
        routeUpdates.page = null;
      }

      if (Object.keys(routeUpdates).length > 0) {
        updateAddMoreItemsRoute({
          location,
          navigate,
          updates: routeUpdates,
        });
      }

      setFilters((prevFilters) => {
        const nextFilters = { ...prevFilters };

        if ("search" in newFilter) {
          nextFilters.search = newFilter.search || "";
          nextFilters.page = 1;
        }
        if ("availabilityMode" in newFilter) {
          nextFilters.page = 1;
        }
        if ("warehouseIds" in newFilter) {
          nextFilters.warehouseIds = newFilter.warehouseIds;
          nextFilters.page = 1;
        }
        if ("page" in newFilter) {
          nextFilters.page = newFilter.page || 1;
        }
        if ("limit" in newFilter) {
          nextFilters.limit = newFilter.limit || DEFAULT_LIMIT;
        }
        if ("categories" in newFilter) {
          nextFilters.categories = newFilter.categories || [];
        }
        if ("costCenterIds" in newFilter) {
          nextFilters.costCenterIds = newFilter.costCenterIds;
          nextFilters.page = 1;
        }

        return nextFilters;
      });
    },
    [hideSearch, location, navigate]
  );

  const handleSortChange = useCallback((newOrder) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      order: newOrder || DEFAULT_ADD_MORE_ITEMS_SORT,
    }));
  }, []);

  return (
    <>
      <Grid container spacing={1}>
        {!hideSearch && (
          <Grid size={12}>
            <Filter
              categories={categoryOptions}
              filter={{ search: searchQuery }}
              disabled={loading}
              onChange={onChangeFilter}
            />
          </Grid>
        )}
      </Grid>
      <Box mt={2}>
        <Results
          defaultValues={defaultValues}
          disabled={loading}
          hideDepletedAssets={hideDepletedAssets}
          hasReservedQuantity={hasReservedQuantity}
          showReservedFilter={!!user.company.settings?.reservations}
          onChange={onChangeFilter}
          loading={loading}
          assets={assets}
          pagination={metadata}
          onAssetUpdated={onItemAdded}
          processNewItem={processNewItem}
          warehouses={warehouses}
          selectedWarehouseIds={selectedWarehouseIds}
          costCenters={costCenters}
          selectedCostCenterIds={selectedCostCenterIds}
          order={order}
          setOrder={handleSortChange}
          project={project}
          centerFilters={centerFilters}
          cardsPerRow={cardsPerRow}
        />
      </Box>
    </>
  );
};

export default ({ projectId, onItemAdded, processNewItem }) => (
  <ShoppingProvider>
    <ProjectBrowseView
      projectId={projectId}
      onItemAdded={onItemAdded}
      processNewItem={processNewItem}
    />
  </ShoppingProvider>
);
